import {
  type createAdminUserBody,
  type createOwnerUserBody,
  type createSuperAdminUserBody,
  type createTeamMemberUserBody,
  USER_TYPE_ENUM,
} from "@nlc/shared-types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { usersKeys } from "@/hooks/queryKeys";
import apiClient from "@/lib/api/client";

import { errorToast, successToast } from "../utils/feebackToast";

export function useCreateAdmin() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: createAdminUserBody) =>
      apiClient.url(`/users`).post({ data, type: USER_TYPE_ENUM.ADMIN }).json(),
    mutationKey: ["create-admin"],
    onError: (err) => errorToast(err),
    onSuccess: async () => {
      successToast();
      navigate({ to: ".." });

      return queryClient.invalidateQueries({
        queryKey: usersKeys.all,
      });
    },
  });
}

export function useCreateCollaborator() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: createAdminUserBody) =>
      apiClient
        .url(`/users`)
        .post({ data, type: USER_TYPE_ENUM.COLLABORATOR })
        .json(),
    mutationKey: ["create-collaborator"],
    onError: (err) => errorToast(err),
    onSuccess: async () => {
      successToast();
      navigate({ to: ".." });

      return queryClient.invalidateQueries({
        queryKey: usersKeys.all,
      });
    },
  });
}

export function useCreateOwner() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: createOwnerUserBody) =>
      apiClient.url(`/users`).post({ data, type: USER_TYPE_ENUM.OWNER }).json(),
    mutationKey: ["create-owner"],
    onError: (err) => errorToast(err),
    onSuccess: async () => {
      successToast();
      navigate({ to: ".." });

      return queryClient.invalidateQueries({
        queryKey: usersKeys.all,
      });
    },
  });
}

export function useCreateTeamMember(type: USER_TYPE_ENUM) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      data: createSuperAdminUserBody | createTeamMemberUserBody,
    ) => apiClient.url(`/users`).post({ data, type }).json(),
    mutationKey: ["create-team-member", type],
    onError: (err) => errorToast(err),
    onSuccess: async () => {
      successToast();
      navigate({ to: ".." });

      return queryClient.invalidateQueries({
        queryKey: usersKeys.all,
      });
    },
  });
}
